import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";

import LoginLayout from "../../layouts/LoginLayout";
import { useNavigate, useSearchParams } from "react-router-dom";
import { InputReactHookForm } from "../../components/input/InputReactHookForm";
import alertService from "../../components/alert/alertService";
import UsersService from "../../service/users.service";
import { useAuth } from "../../contexts/UserContext";

import Link from "@mui/material/Link";
import { common } from "../../i18n";
import { EMAIL_REGEX, PASSWORD_REGEX } from "../../constants/form";
import { LOGO_IMG_SRC } from "../../constants";

type ActivateFormValues = {
  email: string;
  password: string;
  repassword: string;
};

const ActivatePage: React.FC = () => {
  const { t } = useTranslation([common]);

  const navigate = useNavigate();

  const { logout, login, user } = useAuth();

  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");

  const {
    handleSubmit,
    control,
    watch,
    formState: { isValid },
  } = useForm<ActivateFormValues>({
    mode: "all",
    reValidateMode: "onChange",
  });

  const onSubmit = async (values: ActivateFormValues) => {
    if (!token) return;

    try {
      const formData = {
        email: values.email ?? "",
        password: values.password ?? "",
      };

      const result = await UsersService.activate(token, formData);

      if (result) {
        await alertService.success(t("activate-your-account"));

        // ต้องรอ logout เคลียร์ session เก่าให้เสร็จก่อน ไม่งั้นอาจไปลบ session ใหม่ที่ login() เพิ่งสร้างทิ้ง
        await logout();

        await login(values.email, values.password);

        navigate("/select-organization");
      }
    } catch (error: any) {
      console.error(error);
      alertService.error(t("Please-Check-Your-Email-Or-Activate-Link"));
    }
  };

  if (user) {
    logout();
    return null;
  }

  if (!token) {
    return (
      <LoginLayout>
        <Box
          maxWidth={"400px"}
          display={"flex"}
          flexDirection={"column"}
          alignItems="center"
          rowGap={"20px"}
        >
          <img
            src={LOGO_IMG_SRC}
            alt="logo"
            style={{ height: "28px", marginBottom: "20px" }}
          />
          <Typography variant="h5" color="error">
            {t("Invalid-Activation-Link")}
          </Typography>
          <Typography variant="body2" textAlign="center">
            {t("Please-Check-Your-Email-Again")}
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigate("/login")}
            sx={{ mt: 2 }}
          >
            {t("Go-To-Login-Page")}
          </Button>
        </Box>
      </LoginLayout>
    );
  }

  return (
    <LoginLayout>
      <Box
        maxWidth={"400px"}
        display={"flex"}
        flexDirection={"column"}
        rowGap={"20px"}
        width={"100%"}
      >
        <img
          src={LOGO_IMG_SRC}
          alt="logo"
          style={{
            padding: "12px 0px",
            height: "28px",
            width: "233px",
            marginBottom: "20px",
          }}
        />

        <Typography variant="h1">{t("activate-your-account")}</Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              width: "100%",
            }}
            padding={"24px 0px"}
          >
            <InputReactHookForm
              required
              rules={{
                required: t("Must-be-a-valid-email"),
                pattern: {
                  value: EMAIL_REGEX,
                  message: t("Must-be-a-valid-email"),
                },
              }}
              name={"email"}
              control={control}
              label={t("Email")}
              info={t("Email")}
              placeholder={t("Email")}
              defaultValue={""}
            />

            <InputReactHookForm
              required
              rules={{
                required: t("Password-must-be-at-least-6-characters"),
                pattern: {
                  value: PASSWORD_REGEX,
                  message: t("password-fails-to-match-pattern"),
                },
              }}
              control={control}
              name={"password"}
              label={t("Password")}
              info={t("password-fails-to-match-pattern")}
              placeholder={t("Password")}
              type="password"
            />

            <InputReactHookForm
              required
              rules={{
                required: t("Password-must-be-at-least-6-characters"),
                validate: () => {
                  if (watch("password") !== watch("repassword")) {
                    return t("password-and-repassword-not-equal");
                  }
                  return true;
                },
              }}
              control={control}
              name={"repassword"}
              label={t("Confirm-Your-New-Password")}
              info={t("Confirm-Your-New-Password")}
              placeholder={t("Confirm-Your-New-Password")}
              type="password"
            />
          </Box>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            color={"secondary"}
            disabled={!isValid}
            sx={{
              marginBottom: "20px",
            }}
          >
            {t("activated")}
          </Button>

          <Link variant="body1" href="/login" underline="hover" color="primary">
            {t("Go-To-Login-Page")}
          </Link>
        </form>
      </Box>
    </LoginLayout>
  );
};

export default ActivatePage;
